// src/modules/working_hours/domain/AbsenceValidator.ts

import { Absence } from './Absence';

export interface AbsenceFormData {
  type: string | null;
  reason: string;
  startTime: string;
  endTime: string;
  date: string;
}

export interface ValidationErrors {
  type?: string;
  reason?: string;
  startTime?: string;
  endTime?: string;
  date?: string;
}

export class AbsenceValidator {
  private static readonly TIME_REGEX = /^([0-1]\d|2[0-3]):([0-5]\d)$/;
  private static readonly DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

  /**
   * Valida todos los campos del formulario de ausencia
   * Retorna un objeto con los errores encontrados
   */
  static validate(
    formData: AbsenceFormData,
    existingAbsences: Absence[] = []
  ): ValidationErrors {
    const errors: ValidationErrors = {};

    // 1. Validar tipo de ausencia
    if (!formData.type) {
      errors.type = 'Debe seleccionar un tipo de ausencia';
    }

    // 2. Validar motivo
    if (!formData.reason || !formData.reason.trim()) {
      errors.reason = 'Debe escribir el motivo de la ausencia';
    }

    // 3. Validar hora inicio
    if (!formData.startTime) {
      errors.startTime = 'Debe seleccionar hora de inicio';
    } else if (!this.TIME_REGEX.test(formData.startTime)) {
      errors.startTime = 'Hora inválida (formato HH:mm)';
    }

    // 4. Validar hora fin
    if (!formData.endTime) {
      errors.endTime = 'Debe seleccionar hora de fin';
    } else if (!this.TIME_REGEX.test(formData.endTime)) {
      errors.endTime = 'Hora inválida (formato HH:mm)';
    }

    // 5. Validar fecha
    if (!formData.date) {
      errors.date = 'Debe seleccionar una fecha';
    } else if (!this.DATE_REGEX.test(formData.date)) {
      errors.date = 'Formato inválido (DD/MM/YYYY)';
    }

    // Si hay errores de formato, no continuar con validaciones de lógica de negocio
    if (Object.keys(errors).length > 0) {
      return errors;
    }

    // 6. Validar que hora fin > hora inicio
    const { startDate, endDate } = this.createDateObjects(formData);

    if (endDate <= startDate) {
      errors.startTime = 'Verifica las horas';
      errors.endTime = 'La hora fin debe ser mayor que la hora inicio';
      return errors;
    }

    // 7. Validar solapamiento solo con otras ausencias normales (ignorar vacaciones)
    for (const existingAbsence of existingAbsences) {
      // Ignorar vacaciones - las ausencias normales pueden coexistir con vacaciones
      if (existingAbsence.tipoAusencia === 'Vacaciones') {
        continue;
      }

      const existingStart = new Date(existingAbsence.start);
      const existingEnd = new Date(existingAbsence.end);

      // Verificar si hay solapamiento
      const hasOverlap = !(endDate <= existingStart || startDate >= existingEnd);

      if (hasOverlap) {
        errors.startTime = 'Conflicto con otra ausencia';
        errors.endTime = 'Conflicto con otra ausencia';
        errors.date = 'Ya tienes una ausencia en este día';
        return errors;
      }
    }

    return errors;
  }

  /**
   * Crea objetos Date a partir de los datos del formulario
   */
  static createDateObjects(formData: AbsenceFormData): {
    startDate: Date;
    endDate: Date;
  } {
    const dateParts = formData.date.split('/').map(Number);
    const day = dateParts[0];
    const month = dateParts[1];
    const year = dateParts[2];

    const startTimeParts = formData.startTime.split(':').map(Number);
    const startHour = startTimeParts[0];
    const startMinute = startTimeParts[1];

    const endTimeParts = formData.endTime.split(':').map(Number);
    const endHour = endTimeParts[0];
    const endMinute = endTimeParts[1];

    const startDate = new Date(year, month - 1, day, startHour, startMinute);
    const endDate = new Date(year, month - 1, day, endHour, endMinute);

    return { startDate, endDate };
  }

  /**
   * Verifica si hay errores de validación
   */
  static hasErrors(errors: ValidationErrors): boolean {
    return Object.keys(errors).length > 0;
  }
}
